import React, { useEffect, useRef } from'react';
import { Link } from'react-router-dom';
import { gsap } from'../../animations/gsap.config';
import { ScrollTrigger } from'../../animations/gsap.config';
import servicesData from'../../data/services.json';
import { 
 ArrowRight, 
 CheckCircle, 
 Search, 
 BarChart3, 
 Shield, 
 GraduationCap 
} from'lucide-react';

const ICON_MAP = {
 Search,
 BarChart3,
 Shield,
 GraduationCap,
};

/* ─────────────────────────────────────────────
 DETAILED SERVICE BLOCK
───────────────────────────────────────────── */
const ServiceBlock = ({ service, index }) => {
 const isEven = index % 2 === 0;
 
 const blockRef = useRef(null);
 const imgWrapRef = useRef(null);
 const imgRef = useRef(null);
 const contentRef = useRef(null);
 
 const IconC = ICON_MAP[service.icon] || Search;

 useEffect(() => {
 const ctx = gsap.context(() => {
 /* Scroll Reveal */
 const tl = gsap.timeline({
 scrollTrigger: {
 trigger: blockRef.current,
 start:'top 80%',
 }
 });

 tl.fromTo(imgWrapRef.current, 
 { x: isEven ? -50 : 50, opacity: 0, clipPath: isEven ?'inset(0 100% 0 0)' :'inset(0 0 0 100%)' },
 { x: 0, opacity: 1, clipPath:'inset(0 0% 0 0)', duration: 1, ease:'power4.out' }
 )
 .fromTo(contentRef.current?.children ?? [],
 { y: 30, opacity: 0 },
 { y: 0, opacity: 1, stagger: 0.1, duration: 0.8, ease:'power3.out' },
'-=0.6'
 );

 /* Image Parallax */
 if(imgRef.current) {
 gsap.to(imgRef.current, {
 yPercent: 15,
 ease:'none',
 scrollTrigger: {
 trigger: imgWrapRef.current,
 start:'top bottom',
 end:'bottom top',
 scrub: 1.5
 }
 });
 }
 }, blockRef);

 return () => ctx.revert();
 }, [isEven]);

 return (
 <div 
 ref={blockRef} 
 id={service.id}
 className={`flex flex-col gap-12 lg:gap-20 pt-24 border-t border-white/5 ${isEven ?'lg:flex-row' :'lg:flex-row-reverse'}`}
 >
 {/* ── Image Side ── */}
 <div className="w-full lg:w-5/12 flex-shrink-0 relative">
 <div 
 ref={imgWrapRef}
 className="sticky top-32 rounded-3xl overflow-hidden glass border border-white/10"
 style={{ aspectRatio:'3/4' }}
 >
 <div className="absolute inset-0 bg-gradient-to-t from-[#060d24] via-transparent to-transparent z-10" />
 <div className="absolute inset-0 opacity-40 mix-blend-color z-10" style={{ backgroundColor: service.color }} />
 
 <img 
 ref={imgRef}
 src={service.image} 
 alt={service.title}
 className="absolute inset-0 w-full h-[120%] -top-[10%] object-cover"
 />

 {/* Number Watermark */}
 <div className="absolute -bottom-6 -right-6 font-display font-black text-[120px] leading-none opacity-20 z-20 pointer-events-none select-none" style={{ color: service.color }}>
 {service.number}
 </div>

 {/* Case Study Card */}
 <div className="absolute bottom-6 left-6 right-6 z-30 p-5 rounded-2xl bg-slate-800/90 backdrop-blur-md border border-white/10">
 <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-2 text-slate-400">Featured Case Study</p>
 <p className="text-white font-bold text-sm mb-1">{service.caseStudy.client}</p>
 <p className="text-slate-300 text-xs line-clamp-2">{service.caseStudy.title}</p>
 </div>
 </div>
 </div>

 {/* ── Content Side ── */}
 <div ref={contentRef} className="w-full lg:w-7/12 space-y-8 pb-10">
 
 {/* Header */}
 <div className="space-y-4">
 <div 
 className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
 style={{ backgroundColor:`${service.color}15`, border:`1px solid ${service.color}30`, color: service.color }}
 >
 <IconC size={32} strokeWidth={1.5} />
 </div>
 <div>
 <p className="text-xs font-mono font-bold tracking-[0.2em] mb-2 uppercase" style={{ color: service.color }}>
 Service {service.number}
 </p>
 <h2 className="font-display font-black text-4xl md:text-5xl text-slate-800 mb-3 tracking-tight">
 {service.title}
 </h2>
 <h3 className="text-xl md:text-2xl font-light text-slate-600 italic">
 {service.tagline}
 </h3>
 </div>
 </div>

 <div className="h-px w-full bg-white/10" />

 {/* Description */}
 <p className="text-slate-600 text-base leading-relaxed md:text-lg font-light">
 {service.fullDesc}
 </p>

 {/* Features Grid */}
 <div className="pt-4">
 <h4 className="text-sm font-bold tracking-widest text-slate-800 uppercase mb-6">Key Offerings</h4>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 {service.features.map((feat, i) => (
 <div key={i} className="flex items-start gap-3">
 <CheckCircle size={18} style={{ color: service.color }} className="shrink-0 mt-0.5" />
 <span className="text-slate-600 text-sm leading-snug">{feat}</span>
 </div>
 ))}
 </div>
 </div>

 {/* Methodologies & Sectors */}
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-6">
 <div className="glass p-5 rounded-2xl border border-white/5">
 <h4 className="text-[10px] font-bold tracking-[0.2em] text-slate-500 uppercase mb-4">Methodologies</h4>
 <div className="flex flex-wrap gap-2">
 {service.methodologies.map((m, i) => (
 <span key={i} className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 bg-white/5 text-slate-600 rounded">
 {m}
 </span>
 ))}
 </div>
 </div>
 <div className="glass p-5 rounded-2xl border border-white/5">
 <h4 className="text-[10px] font-bold tracking-[0.2em] text-slate-500 uppercase mb-4">Focus Sectors</h4>
 <div className="flex flex-wrap gap-2">
 {service.sectors.map((s, i) => (
 <span key={i} className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 text-slate-600 rounded" style={{ backgroundColor:`${service.color}20` }}>
 {s}
 </span>
 ))}
 </div>
 </div>
 </div>

 {/* Stats Bar */}
 <div className="flex items-center justify-between border-y border-white/5 py-6 mt-8">
 <div className="text-center">
 <div className="text-2xl font-bold font-mono text-slate-800 mb-1">{service.stats.projects}</div>
 <div className="text-[10px] font-bold tracking-[0.2em] text-slate-500 uppercase">Projects</div>
 </div>
 <div className="w-px h-10 bg-white/10" />
 <div className="text-center">
 <div className="text-2xl font-bold font-mono text-slate-800 mb-1">{service.stats.regions}</div>
 <div className="text-[10px] font-bold tracking-[0.2em] text-slate-500 uppercase">Regions</div>
 </div>
 <div className="w-px h-10 bg-white/10" />
 <div className="text-center">
 <div className="text-2xl font-bold font-mono text-slate-800 mb-1">{service.stats.accuracy}</div>
 <div className="text-[10px] font-bold tracking-[0.2em] text-slate-500 uppercase">Accuracy</div>
 </div>
 </div>

 </div>
 </div>
 );
};

/* ─────────────────────────────────────────────
 MAIN COMPONENT
───────────────────────────────────────────── */
const ServiceShowcase = () => {
 const { showcase } = servicesData;

 return (
 <section id="service-showcase" className="relative bg-white pb-24">
 <div className="container-fluid px-4 md:px-8 max-w-7xl mx-auto space-y-12 lg:space-y-0">
 {showcase.services.map((service, i) => (
 <ServiceBlock key={service.id} service={service} index={i} />
 ))}
 </div>
 </section>
 );
};

export default ServiceShowcase;
